import { useCallback, useRef } from 'react';

const DEFAULT_MS = 400;
const DEFAULT_HINT_WINDOW_MS = 3000;

/** Double-click (desktop) or double-tap (touch) within a short window. */
export function useDoubleActivate(onActivate, delayMs = DEFAULT_MS) {
  const lastRef = useRef(0);

  return useCallback((event) => {
    const now = Date.now();
    if (now - lastRef.current <= delayMs) {
      lastRef.current = 0;
      onActivate(event);
      return;
    }
    lastRef.current = now;
  }, [onActivate, delayMs]);
}

/**
 * Double-activate with hint when two taps/clicks land within hintWindowMs
 * but outside the strict double-activate interval.
 */
export function useDoubleActivateWithHint(
  onActivate,
  {
    delayMs = DEFAULT_MS,
    hintWindowMs = DEFAULT_HINT_WINDOW_MS,
    onHint,
  } = {},
) {
  const lastRef = useRef(0);
  const attemptStartRef = useRef(0);
  const attemptCountRef = useRef(0);

  return useCallback((event) => {
    const now = Date.now();

    if (lastRef.current && now - lastRef.current <= delayMs) {
      lastRef.current = 0;
      attemptStartRef.current = 0;
      attemptCountRef.current = 0;
      onActivate(event);
      return;
    }

    lastRef.current = now;

    if (!attemptStartRef.current || now - attemptStartRef.current > hintWindowMs) {
      attemptStartRef.current = now;
      attemptCountRef.current = 1;
      return;
    }

    attemptCountRef.current += 1;
    if (attemptCountRef.current >= 2) {
      onHint?.();
      attemptStartRef.current = 0;
      attemptCountRef.current = 0;
    }
  }, [onActivate, delayMs, hintWindowMs, onHint]);
}